<script setup>
import { ref, computed, inject } from 'vue'
import { useStore } from 'vuex'
import InputNumber from 'primevue/inputnumber'
import ProgressiveImageUploader from '@/views/appLivraisons/ProgressiveImageUploader.vue'
import axios from '@/api/axios'
import dataService from '@/services/dataService'

// Props
const props = defineProps({
  delivery: {
    type: Object,
    required: true
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

// Store
const store = useStore()
const isOnline = computed(() => store.getters['sync/isOnline'])

// Bus d'événements
const eventBus = inject('eventBus')

// Références pour nos uploaders
const fruitsUploader = ref(null)
const emplacementUploader = ref(null)
const consignesUploader = ref(null)

// État local pour les états des images
const fruitsUploaded = ref(false)
const emplacementUploaded = ref(false)
const consignesUploaded = ref(false)

// État local pour les IDs d'images côté serveur
const fruitsServerId = ref(null)
const emplacementServerId = ref(null)
const consignesServerId = ref(null)

// État local pour les données des images
const fruitsImageData = ref(null)
const emplacementImageData = ref(null)
const consignesImageData = ref(null)

// Nombre de cageots récupérés
const cageotsCount = ref(null)

// Computed
const isConsignesRequired = computed(() => cageotsCount.value > 0)

// Validation du formulaire - exposée pour le composant parent
const isFormValid = computed(() => {
  // Vérification de base pour les photos obligatoires
  const baseValidation = (
    fruitsUploaded.value &&
    emplacementUploaded.value 
  )
  
  // Vérifier que l'utilisateur a explicitement entré une valeur pour les cageots
  const cageotsValid = cageotsCount.value !== null
  
  // Si des cageots sont déclarés, la photo est requise
  if (isConsignesRequired.value) {
    return baseValidation && consignesUploaded.value && cageotsValid
  }
  
  // Si pas de cageots, pas besoin de photo de consignes mais toujours besoin d'une valeur explicite
  return baseValidation && cageotsValid
})

// Vérifier si tous les uploads sont terminés (Online mode)
const allUploadsComplete = computed(() => {
  if (!isOnline.value) return true; // En mode hors ligne, on considère tout comme complet
  
  const fruitsComplete = fruitsServerId.value !== null;
  const emplacementComplete = emplacementServerId.value !== null;
  
  // Pour les consignes, on vérifie si elles sont requises
  if (isConsignesRequired.value) {
    return fruitsComplete && emplacementComplete && (consignesServerId.value !== null);
  }
  
  return fruitsComplete && emplacementComplete;
})

// Méthodes pour gérer les uploads réussis (prise de photo)
const handleFruitsSuccess = (imageId, imageData) => {
  fruitsUploaded.value = true
  fruitsImageData.value = imageData
}

const handleEmplacementSuccess = (imageId, imageData) => {
  emplacementUploaded.value = true
  emplacementImageData.value = imageData
}

const handleConsignesSuccess = (imageId, imageData) => {
  consignesUploaded.value = true
  consignesImageData.value = imageData
}

// Méthodes pour gérer les uploads complets (images envoyées au serveur)
const handleFruitsUploadComplete = (serverId, imageData) => {
  fruitsServerId.value = serverId
  // Mettre à jour les données avec l'ID du serveur
  if (fruitsImageData.value) {
    fruitsImageData.value.serverImageId = serverId
  }
}

const handleEmplacementUploadComplete = (serverId, imageData) => {
  emplacementServerId.value = serverId
  // Mettre à jour les données avec l'ID du serveur
  if (emplacementImageData.value) {
    emplacementImageData.value.serverImageId = serverId
  }
}

const handleConsignesUploadComplete = (serverId, imageData) => {
  consignesServerId.value = serverId
  // Mettre à jour les données avec l'ID du serveur
  if (consignesImageData.value) {
    consignesImageData.value.serverImageId = serverId
  }
}

// Méthode pour gérer les erreurs d'upload
const handleUploadError = (error) => {
  console.error('Upload error:', error)
}

// Méthode de confirmation de la livraison
const handleConfirm = async () => {
  if (!isFormValid.value) return false

  try {
    // Si nous sommes en ligne et que les uploaders ont déjà envoyé les images
    // nous collectons les IDs serveur directement
    if (isOnline.value && allUploadsComplete.value) {
      console.log('Toutes les images déjà uploadées, utilisation des IDs serveur');
      
      // Préparation des IDs d'images
      let serverImageIds = [];
      
      // Ajouter les IDs serveur si disponibles
      if (fruitsServerId.value) serverImageIds.push(fruitsServerId.value);
      if (emplacementServerId.value) serverImageIds.push(emplacementServerId.value);
      if (isConsignesRequired.value && consignesServerId.value) {
        serverImageIds.push(consignesServerId.value);
      }
      
      // Vérifie que cageotsCount.value est un nombre valide, sinon utilise 0
      const finalCageotsCount = cageotsCount.value !== null && !isNaN(cageotsCount.value) 
        ? cageotsCount.value 
        : 0
      
      console.log('Envoi direct avec les IDs serveur:', serverImageIds);
      
      // Mise à jour directe en utilisant PATCH
      try {
        const updatePayload = {
          status: 'DELIVERED',
          undone_reason: null,
          image_ids: serverImageIds,
          consignes_rendues_v2: finalCageotsCount
        };
        
        await store.dispatch('logs/info', {
          message: `Mise à jour directe de la livraison ${props.delivery.id}`,
          data: { 
            status: 'DELIVERED',
            imageIds: serverImageIds
          }
        });
        
        // Appeler l'API directement
        const response = await axios.patch(`/livraison/delivery/${props.delivery.id}/`, updatePayload);
        
        console.log('Livraison mise à jour avec succès (direct):', response.data);
        
        // IMPORTANT: Mettre à jour également le state local pour refléter le changement
        // Mise à jour de la livraison dans la tournée
        store.commit('UPDATE_DELIVERY_IN_TOURNEE', {
          deliveryId: props.delivery.id,
          updates: {
            status: 'DELIVERED',
            undone_reason: null,
            consignes_rendues_v2: finalCageotsCount
          }
        });
        
        // Mise à jour du cache local
        await store.dispatch('sync/refreshPendingUpdates');
        
        // Recharger la livraison depuis le serveur pour être sûr d'avoir les données à jour
        if (isOnline.value) {
          try {
            const freshData = await axios.get(`/livraison/delivery/${props.delivery.id}/`);
            if (freshData && freshData.data) {
              // Mettre à jour la livraison dans la tournée avec les données fraîches
              store.commit('UPDATE_DELIVERY_IN_TOURNEE', {
                deliveryId: props.delivery.id,
                updates: freshData.data
              });
              
              // Mettre à jour la livraison dans le dataService
              await dataService.saveDelivery(props.delivery.id, freshData.data);
              
              // Émettre un événement pour informer les autres composants de la mise à jour
              if (eventBus) {
                eventBus.deliveryUpdated(props.delivery.id);
              }
            }
          } catch (refreshError) {
            console.warn('Impossible de recharger les données fraîches:', refreshError);
            // Ne pas bloquer le processus si la récupération échoue
          }
        }
        
        emit('success', {
          status: 'DELIVERED',
          consignes_rendues_v2: finalCageotsCount
        });
        
        return true;
      } catch (error) {
        console.error('Erreur lors de la mise à jour directe:', error);
        // En cas d'erreur, on retombe sur le mécanisme standard
      }
    }
    
    // Si nous sommes hors ligne ou que les images n'ont pas pu être uploadées directement,
    // on utilise le mécanisme standard avec les données locales
    console.log('Utilisation du mécanisme de mise à jour standard');
    
    // Préparation des images à envoyer
    let allImages = []
    
    // Ajouter l'image des fruits si disponible
    if (fruitsImageData.value) {
      allImages.push({
        id: fruitsImageData.value.id,
        file: fruitsImageData.value.file,
        context: 'fruits',
        // Si l'image a déjà été uploadée, inclure l'ID serveur
        serverImageId: fruitsServerId.value
      })
    }
    
    // Ajouter l'image de l'emplacement si disponible
    if (emplacementImageData.value) {
      allImages.push({
        id: emplacementImageData.value.id,
        file: emplacementImageData.value.file,
        context: 'emplacement',
        // Si l'image a déjà été uploadée, inclure l'ID serveur
        serverImageId: emplacementServerId.value
      })
    }
    
    // Ajouter les photos de consignes seulement si nécessaire
    if (isConsignesRequired.value && consignesImageData.value) {
      allImages.push({
        id: consignesImageData.value.id,
        file: consignesImageData.value.file,
        context: 'consignes',
        // Si l'image a déjà été uploadée, inclure l'ID serveur
        serverImageId: consignesServerId.value
      })
    }

    // Vérifie que cageotsCount.value est un nombre valide, sinon utilise 0
    const finalCageotsCount = cageotsCount.value !== null && !isNaN(cageotsCount.value) 
      ? cageotsCount.value 
      : 0
    
    console.log('Envoi des données pour validation de livraison...')

    // Mise à jour du statut de livraison via le store avec consignes_rendues_v2
    const result = await store.dispatch('updateDeliveryStatus', {
      deliveryId: props.delivery.id,
      status: 'DELIVERED',
      consignes_rendues_v2: finalCageotsCount,
      images: allImages,
      waitForSync: false // On ne force pas l'attente car les images sont déjà en cours d'upload
    })

    if (result.success) {
      console.log('Livraison validée avec succès')
      
      // S'assurer que l'interface est mise à jour, même en mode hors ligne
      // Dans tous les cas mettre à jour la livraison dans le cache local
      try {
        // Lire la livraison actuelle du cache (ce qui inclut déjà les modifications)
        const updatedDelivery = await dataService.getDelivery(props.delivery.id);
        
        // Mettre à jour le cache local
        if (updatedDelivery) {
          await dataService.saveDelivery(props.delivery.id, {
            ...updatedDelivery,
            status: 'DELIVERED',
            undone_reason: null,
            consignes_rendues_v2: finalCageotsCount
          });
          
          // Émettre un événement pour informer les autres composants de la mise à jour
          if (eventBus) {
            eventBus.deliveryUpdated(props.delivery.id);
          }
        }
      } catch (err) {
        // Ne pas bloquer si cette opération échoue
        console.warn('Erreur lors de la mise à jour du cache local:', err);
      }
      
      emit('success', {
        status: 'DELIVERED',
        consignes_rendues_v2: finalCageotsCount
      })
      return true
    }
  } catch (error) {
    console.error('Error in handleConfirm:', error)
  }

  return false
}

// Expose les propriétés et méthodes nécessaires pour le composant parent
defineExpose({
  isFormValid,
  handleConfirm
})
</script>

<template>
  <div class="conforme-modal-content" v-if="delivery">
    <div class="conforme-form">
      <!-- Bandeau d'attention -->
      <div class="attention-banner">
        <i class="pi pi-info-circle"></i>
        <span>Prenez les photos AVANT de remettre les fruits de la semaine précédente dans les paniers pour éviter tout litige avec les clients.</span>
      </div>

      <!-- Section des photos (côte à côte) -->
      <div class="photos-row">
        <!-- Section Upload Fruits -->
        <div class="upload-section">
          <div class="section-header">
            <h4>Photo des fruits</h4>
            <small>Prendre le contenu du cageot en photo</small>
          </div>
          <ProgressiveImageUploader 
            ref="fruitsUploader"
            :delivery-id="delivery.id" 
            delivery-context="fruits"
            @success="handleFruitsSuccess" 
            @error="handleUploadError"
            @upload-complete="handleFruitsUploadComplete"
          />
        </div>

        <!-- Section Upload Emplacement -->
        <div class="upload-section">
          <div class="section-header">
            <h4>Photo de l'emplacement</h4>
            <small>Reculer de 1 metre et prendre le cageot dans son environnement</small>
          </div>
          <ProgressiveImageUploader 
            ref="emplacementUploader"
            :delivery-id="delivery.id" 
            delivery-context="emplacement"
            @success="handleEmplacementSuccess" 
            @error="handleUploadError"
            @upload-complete="handleEmplacementUploadComplete"
          />
        </div>
      </div>

      <!-- Section Cageots -->
      <div class="upload-section">
        <div class="section-header">
          <h4>Cageots récupérés</h4>
        </div>
        <div class="cageots-counter">
          <label for="cageots-count">Nombre de cageots récupérés: <span class="required">*</span></label>
          <InputNumber 
            id="cageots-count"
            v-model="cageotsCount" 
            :min="0" 
            :showButtons="false"
            class="input-number-container"
            placeholder="Nombre de cageots"
            :required="true"
          />
        </div>

        <!-- Section Upload Consignes (conditionnelle) -->
        <div v-if="isConsignesRequired" class="consignes-section">
          <div class="section-header">
            <h4>Photo des cageots rendus</h4>
            <small>Prendre en photo le ou les cageots vides récupérés</small>
          </div>
          <ProgressiveImageUploader 
            ref="consignesUploader"
            :delivery-id="delivery.id" 
            delivery-context="consignes"
            @success="handleConsignesSuccess"
            @error="handleUploadError"
            @upload-complete="handleConsignesUploadComplete"
          />
        </div>
      </div>
      
      <!-- Indicateur de statut d'upload -->
      <div v-if="isOnline && (fruitsUploaded || emplacementUploaded || consignesUploaded)" class="upload-status-indicator">
        <div class="status-header">
          <i :class="allUploadsComplete ? 'pi pi-check-circle status-icon complete' : 'pi pi-sync status-icon uploading'" 
             :style="!allUploadsComplete ? { animation: 'spin 2s linear infinite' } : {}">
          </i>
          <span class="status-text">{{ allUploadsComplete ? 'Toutes les photos sont synchronisées' : 'Synchronisation des photos en cours...' }}</span>
        </div>
        <div class="status-details" v-if="!allUploadsComplete">
          <div v-if="fruitsUploaded" class="status-item" :class="{ complete: fruitsServerId }">
            <i :class="fruitsServerId ? 'pi pi-check-circle' : 'pi pi-clock'"></i>
            <span>Photo des fruits</span>
          </div>
          <div v-if="emplacementUploaded" class="status-item" :class="{ complete: emplacementServerId }">
            <i :class="emplacementServerId ? 'pi pi-check-circle' : 'pi pi-clock'"></i>
            <span>Photo de l'emplacement</span>
          </div>
          <div v-if="consignesUploaded && isConsignesRequired" class="status-item" :class="{ complete: consignesServerId }">
            <i :class="consignesServerId ? 'pi pi-check-circle' : 'pi pi-clock'"></i>
            <span>Photo des cageots rendus</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="error-message">
    Données de livraison non disponibles
  </div>
</template>

<style scoped>
/* Style CSS optimisé pour exploiter la largeur maximale et s'adapter au mobile */
.conforme-modal-content {
  padding: 0.5rem;
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.conforme-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

/* Bandeau d'attention */
.attention-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.65rem 0.75rem;
  background-color: #fef3c7;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #92400e;
  line-height: 1.4;
}

.attention-banner i {
  color: #f59e0b;
  font-size: 0.9rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.attention-banner span {
  flex: 1;
}

/* Disposition optimisée des photos en ligne */
.photos-row {
  display: flex;
  gap: 0.5rem;
  width: 100%;
}

.photos-row .upload-section {
  flex: 1;
  min-width: 0;
  padding: 0.5rem;
}

.upload-section {
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Nouveau conteneur pour l'en-tête des sections */
.section-header {
  margin-bottom: 0.35rem;
}

.upload-section h4 {
  margin: 0;
  font-size: 0.75rem;
  color: #2c3e50;
  font-weight: 700;
  line-height: 1.2;
}

.upload-section small {
  display: block;
  font-size: 0.65rem;
  color: #6c757d;
  margin-top: 0.2rem;
  line-height: 1.2;
  /* Permettre le retour à la ligne pour les textes longs */
  white-space: normal;
  word-wrap: break-word;
}

.consignes-section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #dee2e6;
}

.cageots-counter {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.cageots-counter label {
  font-size: 0.8rem;
  color: #4b5563;
  line-height: 1.2;
  /* Permettre le retour à la ligne pour les textes longs */
  white-space: normal;
  word-wrap: break-word;
}

:deep(.input-number-container) {
  width: 100%;
  max-width: 100%;
}

:deep(.input-number-container .p-inputnumber-input) {
  width: 100%;
  min-width: 0;
  font-size: 0.8rem;
  padding: 0.4rem;
}

.status-indicator {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background-color: #fee2e2;
  color: #ef4444;
  margin-top: 0.25rem;
  text-align: center;
}

.status-indicator.completed {
  background-color: #d1fae5;
  color: #10b981;
}

.error-message {
  padding: 1rem;
  text-align: center;
  color: #ef4444;
  font-weight: 500;
}

.required {
  color: #ef4444;
  margin-left: 2px;
}

.validation-message {
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 4px;
}

/* Style pour l'indicateur de statut d'upload */
.upload-status-indicator {
  margin-top: 0.5rem;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.75rem;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.status-icon {
  font-size: 1.1rem;
}

.status-icon.complete {
  color: #10b981;
}

.status-icon.uploading {
  color: #3b82f6;
}

.status-text {
  font-size: 0.85rem;
  font-weight: 500;
  color: #4b5563;
}

.status-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.status-item i {
  color: #f59e0b;
}

.status-item.complete i {
  color: #10b981;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Ajout de style global pour la modale PrimeVue */
:deep(.p-dialog) {
  max-width: 98vw !important;
  width: 98vw !important;
  margin: 0 auto;
}

:deep(.p-dialog-content) {
  padding: 0 0.5rem 1rem 0.5rem !important;
}

:deep(.p-dialog-header) {
  padding: 1rem 1rem !important;
}

:deep(.p-dialog-footer) {
  padding: 0 0.5rem 0.5rem 0.5rem !important;
}

/* Media queries pour les différentes tailles d'écran */
@media (min-width: 400px) {
  .conforme-modal-content {
    padding: 0.75rem;
  }
  
  .attention-banner {
    padding: 0.75rem 0.85rem;
    font-size: 0.8rem;
  }
  
  .attention-banner i {
    font-size: 0.95rem;
  }
  
  .upload-section h4 {
    font-size: 0.8rem;
  }
  
  .upload-section small {
    font-size: 0.7rem;
  }
  
  .photos-row {
    gap: 0.75rem;
  }
  
  .upload-section {
    padding: 0.65rem;
    gap: 0.5rem;
  }
  
  :deep(.p-dialog-content) {
    padding: 0 0.75rem 1.5rem 0.75rem !important;
  }
}

@media (min-width: 640px) {
  .conforme-modal-content {
    padding: 1rem;
  }
  
  .attention-banner {
    padding: 0.85rem 1rem;
    font-size: 0.85rem;
  }
  
  .attention-banner i {
    font-size: 1rem;
  }
  
  .upload-section {
    padding: 0.85rem;
    gap: 0.75rem;
  }
  
  .upload-section h4 {
    font-size: 0.85rem;
  }
  
  .upload-section small {
    font-size: 0.75rem;
  }
  
  .photos-row {
    gap: 1rem;
  }
  
  .cageots-counter {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  
  .cageots-counter label {
    flex: 0 0 auto;
    margin-right: 0.5rem;
    font-size: 0.875rem;
  }
  
  :deep(.input-number-container) {
    width: auto;
    flex: 1;
    max-width: 150px;
  }
  
  :deep(.input-number-container .p-inputnumber-input) {
    font-size: 0.875rem;
    padding: 0.5rem;
  }
  
  :deep(.p-dialog) {
    max-width: 95vw !important;
    width: 95vw !important;
  }
  
  :deep(.p-dialog-content) {
    padding: 0 1rem 1.5rem 1rem !important;
  }
  
  :deep(.p-dialog-header) {
    padding: 1.25rem 1.25rem !important;
  }
  
  :deep(.p-dialog-footer) {
    padding: 0 1rem 1rem 1rem !important;
  }
}

@media (min-width: 768px) {
  :deep(.p-dialog) {
    max-width: 90vw !important;
    width: 90vw !important;
  }
}

@media (min-width: 992px) {
  :deep(.p-dialog) {
    max-width: 80vw !important;
    width: 80vw !important;
  }
}
</style>